import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HiOutlineEnvelope } from "react-icons/hi2";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import Loader from "../../components/Loader/Loader";
import TagList from "../../components/TagList/TagList";
import PostcardGenerator from "../../components/PostcardGenerator/PostcardGenerator";
import { getStamp, deleteStamp } from "../../utilities/stamps-service";
import DeleteModal from "./DeleteModal";

export default function StampsShowPage({ user }) {
  const { catalogue_id } = useParams();
  const navigate = useNavigate();
  const [stamp, setStamp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [postcardOpen, setPostcardOpen] = useState(false);

  useEffect(() => {
    async function fetchStamp() {
      const response = await getStamp(catalogue_id);
      if (response && typeof response === "object" && response._id) {
        setStamp(response);
      } else {
        console.log("error fetching stamp:", response);
      }
      setIsLoading(false);
    }
    fetchStamp();
  }, [catalogue_id]);

  async function handleDelete() {
    setIsDeleting(true);
    setDeleteError("");
    // deleteStamp returns the Error instead of throwing
    const result = await deleteStamp(stamp._id);
    if (result instanceof Error) {
      console.log("error deleting stamp:", result);
      setIsDeleting(false);
      setDeleteError("Failed to delete this stamp. Please try again.");
      return;
    }
    navigate("/");
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex justify-center items-center py-20">
        <Loader />
      </div>
    );
  }

  if (!stamp) {
    return (
      <section className="min-h-screen bg-neutral-950 w-[90vw] max-w-[900px] mx-auto py-10 text-center">
        <h2 className="text-xl mb-4 text-white">Stamp not found</h2>
        <button
          className="text-sm underline text-gray-400 hover:text-white"
          onClick={() => navigate("/")}
        >
          Return to home
        </button>
      </section>
    );
  }

  const themeNames = stamp.themes?.map((t) => t.themeName) || [];
  const colourNames = stamp.colours?.map((c) => c.colourName) || [];

  const metadataFields = [
    { label: "Catalogue numbers", value: stamp.catalogNumbers },
    { label: "Subject", value: stamp.subject },
    { label: "Year of first issue", value: stamp.year },
    { label: "Denomination", value: stamp.denomination },
    {
      label: "Themes",
      value: themeNames.length > 0 ? themeNames : null,
      render: () => <TagList tags={themeNames} filterType="theme" />,
    },
    {
      label: "Colours",
      value: colourNames.length > 0 ? colourNames : null,
      render: () => <TagList tags={colourNames} filterType="colour" />,
    },
    { label: "Condition", value: stamp.condition },
    { label: "Year postmarked", value: stamp.yearPostmarked },
  ];

  const hasMetadata = metadataFields.some((f) => f.value);

  return (
    <section className="min-h-screen bg-neutral-950 text-white">
      <div className="w-full max-w-[1000px] mx-auto py-8 px-4">
        {/* Admin actions */}
        {user?.isAdmin === true && (
          <div className="flex justify-end gap-3 mb-6">
            <button
              onClick={() => navigate(`/stamps/${catalogue_id}/edit`)}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-600 text-neutral-300 hover:border-white hover:text-white transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-red-800 text-red-400 hover:bg-red-950 hover:border-red-500 transition-colors"
            >
              Delete
            </button>
          </div>
        )}

        {/* Stamp image — centered, prominent */}
        <div className="flex justify-center py-4 md:py-8">
          <img
            className="max-w-full max-h-[85vh] object-contain drop-shadow-[0_4px_24px_rgba(255,255,255,0.08)]"
            src={stamp.image}
            alt={stamp.subject || "Stamp"}
          />
        </div>

        {/* Two-column layout: metadata + description */}
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 pt-4 pb-8">
          {/* Left column — right-aligned metadata */}
          {hasMetadata && (
            <div className="md:w-1/2 flex flex-col items-start md:items-end space-y-1">
              {metadataFields.map(
                (field) =>
                  field.value && (
                    <p
                      key={field.label}
                      className="text-left md:text-right text-base leading-snug"
                    >
                      <span className="text-neutral-500 text-sm tracking-wide">
                        {field.label}:
                      </span>{" "}
                      <span className="text-neutral-100">
                        {field.render ? field.render() : field.value}
                      </span>
                    </p>
                  ),
              )}
            </div>
          )}

          {/* Right column — description */}
          {stamp.description && (
            <div className={hasMetadata ? "md:w-1/2" : "w-full"}>
              <ReactMarkdown
                rehypePlugins={[
                  [
                    rehypeSanitize,
                    {
                      tagNames: ["p", "a", "strong", "em", "br"],
                      attributes: { a: ["href", "target", "rel"] },
                    },
                  ],
                ]}
                components={{
                  p: ({ children }) => (
                    <p className="text-base leading-relaxed mb-4 text-neutral-300">
                      {children}
                    </p>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-stone-400 underline hover:text-stone-300"
                    >
                      {children}
                    </a>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-neutral-100">
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic text-neutral-200">{children}</em>
                  ),
                }}
              >
                {stamp.description}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Postcard Generator (Accordion) */}
        <div className="mt-10">
          <button
            className="cursor-pointer flex justify-center w-full"
            onClick={() => setPostcardOpen((prev) => !prev)}
          >
            <span className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full border border-neutral-700 bg-neutral-800/60 backdrop-blur-sm hover:bg-neutral-700/80 hover:border-neutral-500 hover:shadow-lg hover:shadow-neutral-900/50 text-neutral-300 hover:text-neutral-100 text-sm font-medium tracking-wide transition-all duration-300 select-none active:scale-[0.97]">
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-300 opacity-60 group-hover:opacity-100 ${postcardOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              <HiOutlineEnvelope className="w-4 h-4" />
              Send a postcard
            </span>
          </button>
          {postcardOpen && (
            <div className="mt-6 animate-[fadeSlideIn_0.3s_ease-out]">
              <PostcardGenerator
                stampImageUrl={stamp.image}
                stampSubject={stamp.subject}
              />
            </div>
          )}
        </div>

        {/* Back link */}
        <div className="mt-8 pb-12 text-center">
          <button
            className="text-sm text-neutral-500 hover:text-white transition-colors"
            onClick={() => navigate("/")}
          >
            &larr; Back to collection
          </button>
        </div>

        {/* Delete confirmation modal */}
        {showDeleteModal && (
          <DeleteModal
            onCancel={() => {
              setShowDeleteModal(false);
              setDeleteError("");
            }}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
            error={deleteError}
          />
        )}
      </div>
    </section>
  );
}
